import { HelpCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { RootState } from '../store';

interface HelpIconProps {
  docPath: string; // Ex: "03-entreprises" ou "03-entreprises#03-3-creation"
  section?: string; // Ex: "creation" pour l'ancre
  className?: string;
  tooltip?: string;
  size?: 'sm' | 'md' | 'lg';
}

export function HelpIcon({ 
  docPath, 
  section, 
  className = "", 
  tooltip,
  size = 'md'
}: HelpIconProps) {
  const { i18n } = useTranslation();
  const currentLang = i18n.language || 'fr';
  const showHelp = useSelector((state: RootState) => state.preferences.showHelp);
  
  // Si l'aide est désactivée, ne rien afficher
  if (!showHelp) {
    return null;
  }
  
  // Construire le chemin de la documentation
  // Si docPath contient déjà un #, on l'utilise tel quel
  // Sinon, on ajoute la section si fournie
  let fullDocPath = docPath;
  if (section && !docPath.includes('#')) {
    fullDocPath = `${docPath}#${section}`;
  }
  
  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    // Empêcher toute propagation d'événement
    e.preventDefault();
    e.stopPropagation();
    
    // Note: stopImmediatePropagation is not available on React synthetic events
    
    // Gérer l'événement natif si disponible
    if (e.nativeEvent) {
      if (typeof e.nativeEvent.stopImmediatePropagation === 'function') {
        e.nativeEvent.stopImmediatePropagation();
      }
      e.nativeEvent.preventDefault();
    }
    
    // Construire l'URL avec le hash pour l'ancre
    const [file, anchor] = fullDocPath.split('#');
    // Utiliser une URL vers le fichier HTML (au lieu de .md)
    let finalUrl = `/docs/manual/${currentLang}/${file}.html`;
    if (anchor) {
      finalUrl += `#${anchor}`;
    }
    
    // Debug: afficher l'URL dans la console
    console.log('Opening documentation:', finalUrl);
    
    // Utiliser setTimeout pour s'assurer que l'événement est complètement traité
    setTimeout(() => {
      // Ouvrir dans un nouvel onglet avec une URL absolue
      const baseUrl = window.location.origin;
      const absoluteUrl = `${baseUrl}${finalUrl}`;
      window.open(absoluteUrl, '_blank', 'noopener,noreferrer');
    }, 0);
    
    return false;
  };

  const sizeClasses = {
    sm: 'w-3 h-3',
    md: 'w-4 h-4',
    lg: 'w-5 h-5'
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      onMouseDown={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
      className={`inline-flex items-center text-gray-400 hover:text-primary-600 transition-colors ${className}`}
      title={tooltip || 'Aide'}
      aria-label={tooltip || 'Aide'}
      style={{ pointerEvents: 'auto' }}
    >
      <HelpCircle className={sizeClasses[size]} />
    </button>
  );
}

